import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FontControls, unwrapFamily } from "@/components/bi/FontStyleControls";
import { Heading1, Rows3, Columns3, Grid3X3, Sigma, MousePointer2, BarChart3, Filter } from "lucide-react";

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <Label className="text-[11px] text-[color:var(--ink-3)] shrink-0">{label}</Label>
      {children}
    </div>
  );
}

function ColorRow({ label, value, onChange, testid, fallback = "#ffffff" }) {
  return (
    <Row label={label}>
      <span className="flex items-center gap-1">
        {value && <button data-testid={`${testid}-clear`} onClick={() => onChange("")} className="text-[9px] text-[color:var(--ink-3)] hover:text-[color:var(--danger)]">clear</button>}
        <input data-testid={testid} type="color" value={value || fallback} onChange={(e) => onChange(e.target.value)}
          className="h-6 w-8 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
      </span>
    </Row>
  );
}

function Group({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
      <Label className="text-[11px] text-[color:var(--ink-2)] font-medium flex items-center gap-1"><Icon className="w-3 h-3" />{title}</Label>
      {children}
    </div>
  );
}

function TotalFmt({ label, value = {}, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] text-[color:var(--ink-3)] font-medium">{label}</Label>
      <div className="flex items-center gap-1.5 flex-wrap">
        <ColorRow label="Fill" testid={`${testid}-bg`} value={value.bg} onChange={(v) => onChange({ bg: v })} />
        <ColorRow label="Text" testid={`${testid}-color`} value={value.color} onChange={(v) => onChange({ color: v })} fallback="#1c2b3a" />
        <Row label="Bold"><Switch data-testid={`${testid}-bold`} checked={value.bold !== false} onCheckedChange={(v) => onChange({ bold: v })} /></Row>
      </div>
    </div>
  );
}

// Item 27: enterprise pivot formatting — values, columns, gridlines, alternate rows, totals, hover.
export function PivotFormatEditor({ opts, patchOpts, config = {} }) {
  const p = opts.pivotOpts || {};
  const set = (patch) => patchOpts({ pivotOpts: { ...p, ...patch } });
  const hdr = p.headerFmt || {};
  const val = p.valuesFmt || {};
  const setHdr = (x) => set({ headerFmt: { ...hdr, ...unwrapFamily(x) } });
  const setVal = (x) => set({ valuesFmt: { ...val, ...unwrapFamily(x) } });
  const measCols = (config.measures || []).map((m) => m.key || m.alias || `${m.agg || "sum"}_${m.field}`);
  const dimCols = (config.dimensions || []).filter((d) => d && (d.key || d.field)).map((d) => d.key || (d.date_part && d.date_part !== "none" ? `${d.field}_${d.date_part}` : d.field));
  const cols = [...new Set([...dimCols, ...measCols])];
  const [selCol, setSelCol] = useState("");
  const cf = (p.colFmt || {})[selCol] || {};
  const setCol = (x) => set({ colFmt: { ...(p.colFmt || {}), [selCol]: { ...cf, ...unwrapFamily(x) } } });
  const clearCol = () => { const c = { ...(p.colFmt || {}) }; delete c[selCol]; set({ colFmt: c }); };
  const alt = p.altRows || {};
  const setAlt = (x) => set({ altRows: { ...alt, ...x } });
  const grid = p.gridlines || {};
  const setGrid = (x) => set({ gridlines: { ...grid, ...x } });

  return (
    <div className="space-y-2" data-testid="pivot-format-editor">
      <Group icon={Heading1} title="Header formatting" testid="pivot-fmt-header">
        <FontControls testid="pivot-hdr" value={hdr} onChange={setHdr} />
        <ColorRow label="Background" testid="pivot-hdr-bg" value={hdr.bg} onChange={(v) => setHdr({ bg: v })} />
      </Group>

      <Group icon={Rows3} title="Values formatting" testid="pivot-fmt-values">
        <FontControls testid="pivot-val" value={val} onChange={setVal} />
        <Row label="Cell padding (px)">
          <Input data-testid="pivot-val-padding" type="number" min={0} placeholder="auto" value={val.padding ?? ""}
            onChange={(e) => setVal({ padding: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface-2)] w-14 h-6 text-[10px]" />
        </Row>
        <Row label="Null display">
          <Input data-testid="pivot-val-null" placeholder="—" value={val.nullDisplay ?? ""} onChange={(e) => setVal({ nullDisplay: e.target.value })} className="bg-[color:var(--surface-2)] w-20 h-6 text-[10px]" />
        </Row>
      </Group>

      <Group icon={Columns3} title="Column formatting (overrides)" testid="pivot-fmt-column">
        <Select value={selCol || "__none__"} onValueChange={(v) => setSelCol(v === "__none__" ? "" : v)}>
          <SelectTrigger data-testid="pivot-col-select" className="bg-[color:var(--surface-2)] h-7 text-xs"><SelectValue placeholder="Pick a column" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Pick a column…</SelectItem>
            {cols.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}{(p.colFmt || {})[c] ? " •" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        {selCol && (
          <>
            <FontControls testid="pivot-col" value={cf} onChange={setCol} />
            <ColorRow label="Background" testid="pivot-col-bg" value={cf.bg} onChange={(v) => setCol({ bg: v })} />
            <Row label="Wrap text"><Switch data-testid="pivot-col-wrap" checked={!!cf.wrap} onCheckedChange={(v) => setCol({ wrap: v })} /></Row>
            <Row label="Width (px)">
              <Input data-testid="pivot-col-width" type="number" min={0} placeholder="auto" value={cf.width ?? ""}
                onChange={(e) => setCol({ width: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface-2)] w-14 h-6 text-[10px]" />
            </Row>
            {measCols.includes(selCol) && (
              <Row label="Decimals">
                <Input data-testid="pivot-col-decimals" type="number" min={0} max={6} placeholder="—" value={cf.decimals ?? ""}
                  onChange={(e) => setCol({ decimals: e.target.value === "" ? undefined : Number(e.target.value) })} className="bg-[color:var(--surface-2)] w-12 h-6 text-[10px]" />
              </Row>
            )}
            <Button data-testid="pivot-col-clear-override" size="sm" variant="ghost" className="h-6 w-full text-[10px] text-[color:var(--danger)]" onClick={clearCol}>
              Remove override
            </Button>
          </>
        )}
      </Group>

      <Group icon={Grid3X3} title="Gridlines" testid="pivot-fmt-gridlines">
        <Row label="Horizontal lines"><Switch data-testid="pivot-grid-h" checked={grid.h !== false} onCheckedChange={(v) => setGrid({ h: v })} /></Row>
        {grid.h !== false && <ColorRow label="Horizontal color" testid="pivot-grid-hcolor" value={grid.hColor} onChange={(v) => setGrid({ hColor: v })} fallback="#dfe5ec" />}
        <Row label="Vertical lines"><Switch data-testid="pivot-grid-v" checked={!!grid.v} onCheckedChange={(v) => setGrid({ v })} /></Row>
        {!!grid.v && <ColorRow label="Vertical color" testid="pivot-grid-vcolor" value={grid.vColor} onChange={(v) => setGrid({ vColor: v })} fallback="#dfe5ec" />}
      </Group>

      <Group icon={BarChart3} title="Data bars" testid="pivot-fmt-databars">
        {measCols.length === 0 && <p className="text-[10px] text-[color:var(--ink-3)]">Add a measure first.</p>}
        {measCols.map((m) => {
          const db = (p.dataBars || {})[m] || {};
          const setDb = (x) => set({ dataBars: { ...(p.dataBars || {}), [m]: { ...db, ...x } } });
          return (
            <div key={m} className="space-y-0.5">
              <Row label={m.replace(/_/g, " ")}><Switch data-testid={`pivot-databar-${m}`} checked={!!db.on} onCheckedChange={(v) => setDb({ on: v })} /></Row>
              {db.on && (
                <div className="pl-2">
                  <ColorRow label="Bar color" testid={`pivot-databar-color-${m}`} value={db.color} onChange={(v) => setDb({ color: v })} fallback="#2B4EFF" />
                  <Row label="Also on totals"><Switch data-testid={`pivot-databar-totals-${m}`} checked={!!db.totals} onCheckedChange={(v) => setDb({ totals: v })} /></Row>
                </div>
              )}
            </div>
          );
        })}
      </Group>

      <Group icon={Filter} title="Header filters" testid="pivot-fmt-filters">
        <Row label="Filter menus on headers"><Switch data-testid="pivot-header-filters" checked={p.headerFilters !== false} onCheckedChange={(v) => set({ headerFilters: v })} /></Row>
        {(Object.keys(p.fieldFilters || {}).length > 0 || Object.keys(p.measureFilters || {}).length > 0) && (
          <Button data-testid="pivot-clear-saved-filters" size="sm" variant="ghost" className="h-6 text-[10px] text-[color:var(--danger)]" onClick={() => set({ fieldFilters: {}, measureFilters: {} })}>Clear saved header filters</Button>
        )}
      </Group>

      <Group icon={Rows3} title="Alternate rows" testid="pivot-fmt-altrows">
        <Row label="Enable zebra rows"><Switch data-testid="pivot-alt-enable" checked={!!alt.enabled} onCheckedChange={(v) => setAlt({ enabled: v })} /></Row>
        {alt.enabled && (
          <>
            <ColorRow label="Odd row fill" testid="pivot-alt-odd" value={alt.oddBg} onChange={(v) => setAlt({ oddBg: v })} fallback="#f6f8fb" />
            <ColorRow label="Even row fill" testid="pivot-alt-even" value={alt.evenBg} onChange={(v) => setAlt({ evenBg: v })} />
            <ColorRow label="Text color" testid="pivot-alt-text" value={alt.textColor} onChange={(v) => setAlt({ textColor: v })} fallback="#33475b" />
          </>
        )}
      </Group>

      <Group icon={Sigma} title="Totals formatting" testid="pivot-fmt-totals">
        <TotalFmt label="Subtotal rows" testid="pivot-sub" value={p.subtotalFmt || {}} onChange={(x) => set({ subtotalFmt: { ...(p.subtotalFmt || {}), ...x } })} />
        <TotalFmt label="Grand total" testid="pivot-grand" value={p.grandFmt || {}} onChange={(x) => set({ grandFmt: { ...(p.grandFmt || {}), ...x } })} />
      </Group>

      <Group icon={MousePointer2} title="Hover" testid="pivot-fmt-hover">
        <ColorRow label="Row hover color" testid="pivot-hover-color" value={p.hoverColor} onChange={(v) => set({ hoverColor: v })} fallback="#eaf2f8" />
      </Group>
    </div>
  );
}
